// pages/admin/FeeManagement.jsx
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { feeAPI, adminAPI } from '../../services/api';

// Helper to extract readable error message from API error
const getErrorMessage = (error) => {
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (data.detail) {
      if (Array.isArray(data.detail)) {
        return data.detail.map((err) => err.msg || JSON.stringify(err)).join(', ');
      }
      return data.detail;
    }
    if (data.message) return data.message;
    return JSON.stringify(data);
  }
  return error.message || 'An error occurred';
};

const FeeManagement = () => {
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [termsForSelectedSession, setTermsForSelectedSession] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');

  // State for receipt templates
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      const session = sessions.find((s) => String(s.id) === String(selectedSession));
      setTermsForSelectedSession(session?.terms || []);
      setSelectedTerm('');
    } else {
      setTermsForSelectedSession([]);
      setSelectedTerm('');
    }
  }, [selectedSession, sessions]);

  // Fetch templates when tab becomes active
  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
    }
  }, [activeTab]);

  const fetchInitialData = async () => {
    try {
      const [categoriesRes, classesRes, sessionsRes] = await Promise.all([
        feeAPI.getFeeCategories(),
        adminAPI.getClasses(),
        adminAPI.getAcademicSessions(),
      ]);
      setCategories(categoriesRes.data);
      setClasses(classesRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const fetchTemplates = async () => {
    setTemplateLoading(true);
    try {
      const res = await feeAPI.getReceiptTemplates();
      setTemplates(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleCreateCategory = async (values, { resetForm, setSubmitting }) => {
    try {
      await feeAPI.createFeeCategory(values);
      toast.success('Fee category created');
      const res = await feeAPI.getFeeCategories();
      setCategories(res.data);
      resetForm();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateStructure = async (values, { resetForm, setSubmitting }) => {
    const payload = {
      ...values,
      due_date: values.due_date ? `${values.due_date}T00:00:00` : null,
    };
    try {
      await feeAPI.createFeeStructure(payload);
      toast.success('Fee structure created');
      resetForm();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateInvoices = async () => {
    if (!selectedClass || !selectedSession) {
      toast.error('Please select class and session');
      return;
    }
    try {
      const res = await feeAPI.generateInvoices(selectedClass, selectedSession, selectedTerm || null);
      toast.success(`Generated ${res.data.invoices_created} invoices`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Receipt template handlers
  const handleUploadTemplate = async (values, { resetForm, setSubmitting }) => {
    const formData = new FormData();
    formData.append('name', values.name);
    formData.append('description', values.description || '');
    formData.append('file', values.file);
    formData.append('is_default', values.is_default ? 'true' : 'false');

    try {
      await feeAPI.uploadReceiptTemplate(formData);
      toast.success('Template uploaded');
      resetForm();
      fetchTemplates();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await feeAPI.updateReceiptTemplate(templateId, { is_default: true });
      toast.success('Default template updated');
      fetchTemplates();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await feeAPI.deleteReceiptTemplate(templateId);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Validation schemas
  const categorySchema = Yup.object({
    name: Yup.string().required('Required'),
    description: Yup.string(),
    is_optional: Yup.boolean(),
  });

  const structureSchema = Yup.object({
    class_id: Yup.number().required('Required'),
    academic_session_id: Yup.number().required('Required'),
    term_id: Yup.number().nullable(),
    fee_category_id: Yup.number().required('Required'),
    amount: Yup.number().positive().required('Required'),
    due_date: Yup.date().nullable(),
  });

  const templateSchema = Yup.object({
    name: Yup.string().required('Required'),
    description: Yup.string(),
    file: Yup.mixed().required('Please select an HTML file'),
    is_default: Yup.boolean(),
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Fee Management</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['categories', 'structures', 'invoices', 'templates'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* ========================== Categories Tab ========================== */}
      {activeTab === 'categories' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Create Fee Category</h2>
          <Formik
            initialValues={{ name: '', description: '', is_optional: false }}
            validationSchema={categorySchema}
            onSubmit={handleCreateCategory}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <Field name="name" className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <Field as="textarea" name="description" className="input-field" rows="2" />
                </div>
                <div className="flex items-center">
                  <Field type="checkbox" name="is_optional" className="mr-2" />
                  <label className="text-sm text-gray-700">Optional fee</label>
                </div>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  Create Category
                </button>
              </Form>
            )}
          </Formik>

          <h2 className="text-lg font-semibold mt-8 mb-4">Existing Categories</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Optional</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((cat) => (
                  <tr key={cat.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{cat.name}</td>
                    <td className="px-6 py-4">{cat.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{cat.is_optional ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================== Structures Tab ========================== */}
      {activeTab === 'structures' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Create Fee Structure</h2>
          <Formik
            initialValues={{
              class_id: '',
              academic_session_id: '',
              term_id: '',
              fee_category_id: '',
              amount: '',
              due_date: '',
            }}
            validationSchema={structureSchema}
            onSubmit={handleCreateStructure}
          >
            {({ isSubmitting, setFieldValue }) => (
              <Form className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Class *</label>
                  <Field as="select" name="class_id" className="input-field">
                    <option value="">Select class</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>{c.class_name}</option>
                    ))}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Academic Session *</label>
                  <Field
                    as="select"
                    name="academic_session_id"
                    className="input-field"
                    onChange={(e) => {
                      const val = e.target.value;
                      setFieldValue('academic_session_id', val);
                      setSelectedSession(val);
                    }}
                  >
                    <option value="">Select session</option>
                    {sessions.map((s) => (
                      <option key={s.id} value={s.id}>{s.academic_session}</option>
                    ))}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Term (optional)</label>
                  <Field as="select" name="term_id" className="input-field">
                    <option value="">All terms</option>
                    {termsForSelectedSession.map((t) => (
                      <option key={t.id} value={t.id}>{t.term}</option>
                    ))}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fee Category *</label>
                  <Field as="select" name="fee_category_id" className="input-field">
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount *</label>
                  <Field type="number" step="0.01" name="amount" className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date (optional)</label>
                  <Field type="date" name="due_date" className="input-field" />
                </div>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  Create Structure
                </button>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {/* ========================== Invoices Tab ========================== */}
      {activeTab === 'invoices' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Generate Invoices for Class</h2>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700">Class *</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="input-field"
              >
                <option value="">Select class</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.class_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Academic Session *</label>
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="input-field"
              >
                <option value="">Select session</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{s.academic_session}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Term (optional)</label>
              <select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="input-field"
              >
                <option value="">All terms</option>
                {termsForSelectedSession.map((t) => (
                  <option key={t.id} value={t.id}>{t.term}</option>
                ))}
              </select>
            </div>
            <button onClick={handleGenerateInvoices} className="btn-primary">
              Generate Invoices
            </button>
          </div>
        </div>
      )}

      {/* ========================== Receipt Templates Tab ========================== */}
      {activeTab === 'templates' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Upload Receipt Template</h2>
          <Formik
            initialValues={{ name: '', description: '', file: null, is_default: false }}
            validationSchema={templateSchema}
            onSubmit={handleUploadTemplate}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Name *</label>
                  <Field name="name" className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <Field as="textarea" name="description" className="input-field" rows="2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">HTML File *</label>
                  <input
                    type="file"
                    accept=".html,.htm"
                    onChange={(e) => setFieldValue('file', e.currentTarget.files[0])}
                    className="input-field"
                  />
                  {values.file && <p className="text-xs text-gray-500 mt-1">{values.file.name}</p>}
                </div>
                <div className="flex items-center">
                  <Field type="checkbox" name="is_default" className="mr-2" />
                  <label className="text-sm text-gray-700">Set as default template</label>
                </div>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  Upload Template
                </button>
              </Form>
            )}
          </Formik>

          <h2 className="text-lg font-semibold mt-8 mb-4">Existing Templates</h2>
          {templateLoading ? (
            <p>Loading templates...</p>
          ) : templates.length === 0 ? (
            <p className="text-gray-500">No templates uploaded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {templates.map((tpl) => (
                    <tr key={tpl.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{tpl.name}</td>
                      <td className="px-6 py-4">{tpl.description || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{tpl.is_default ? '✅ Yes' : 'No'}</td>
                      <td className="px-6 py-4 whitespace-nowrap space-x-2">
                        {!tpl.is_default && (
                          <button
                            onClick={() => handleSetDefault(tpl.id)}
                            className="text-indigo-600 hover:text-indigo-900 text-sm"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteTemplate(tpl.id)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeeManagement;


